/**
 * DynamicArrow - Motion-enabled arrow component
 *
 * Renders electron movement arrows that dynamically follow atoms and lone pairs.
 * For pa type (LP → atom): selects closest LP to destination to minimize crossing.
 */

import { memo, useEffect } from 'react';
import { motion, useTransform } from 'motion/react';
import { useMotion } from '../../contexts/MotionContext';
import { useMolecularSVG } from '../../contexts/MolecularSVGContext';
import { getBondsForAtom } from '../../utils/dragConstraints';
import { useFormatOverride } from '../../rendering/contexts/FormatOverrideContext';
import { useSVGGeneration } from '../../rendering/useSVGGeneration';
import { lonePairs2d } from '@alchemiesolns/layout-2d';
import { CurveMath } from '@alchemiesolns/kaff-arrow';
import { buildCurvePropsPA, buildCurvePropsPB, buildCurvePropsBA, buildCurvePropsBB } from '../arrowCurveBuilder';
import type { ArrowData, ElectronData, ChargeData } from '../../rendering/useSVGGeneration';
import { calculateBondArrowStart } from '../../arrows/bondTubeOffset';
import { useSelection } from '../../selection';
import { useFeedbackWithContext } from '../../feedback';

export interface DynamicArrowProps {
  arrowData: ArrowData;
  allElectronData: ElectronData[];
  allChargeData: ChargeData[];
}

/**
 * Renders an arrow that follows its source and destination during motion
 */
export const DynamicArrow = memo(function DynamicArrow({ arrowData, allElectronData, allChargeData }: DynamicArrowProps) {
  // Handle different arrow types
  if (arrowData.type === 'pa') {
    return <DynamicArrowPA arrowData={arrowData} allElectronData={allElectronData} allChargeData={allChargeData} />;
  } else if (arrowData.type === 'pb') {
    return <DynamicArrowPB arrowData={arrowData} allElectronData={allElectronData} allChargeData={allChargeData} />;
  } else if (arrowData.type === 'ba') {
    return <DynamicArrowBA arrowData={arrowData} allElectronData={allElectronData} allChargeData={allChargeData} />;
  } else if (arrowData.type === 'bb') {
    return <DynamicArrowBB arrowData={arrowData} allElectronData={allElectronData} allChargeData={allChargeData} />;
  }

  return null;
});

/**
 * PA: Lone Pair → Atom
 */
function DynamicArrowPA({ arrowData, allElectronData: _allElectronData, allChargeData: _allChargeData }: DynamicArrowProps) {
  const { getAtomMotion } = useMotion();
  const { workspace } = useMolecularSVG();
  const format = useFormatOverride();

  // Generate unique ID for this arrow
  const arrowId = `arrow-${arrowData.sourceAtomId}-${arrowData.destAtomId}`;

  const selection = useSelection({ optional: true });
  const feedback = useFeedbackWithContext(arrowId);

  // Get MotionValues for source and destination atoms
  const sourceAtomMotion = getAtomMotion(arrowData.sourceAtomId);
  const destAtomMotion = getAtomMotion(arrowData.destAtomId!);

  if (!sourceAtomMotion || !destAtomMotion) {
    return null;
  }

  // Check selection state
  const isSelected = selection?.isSelected(arrowId) ?? false;
  const isHovered = selection?.isHovered(arrowId) ?? false;

  // Sync selection state with FeedbackManager
  useEffect(() => {
    feedback.setSelected(isSelected);
  }, [isSelected, feedback]);

  useEffect(() => {
    feedback.setHovered(isHovered);
  }, [isHovered, feedback]);

  // Get bonds for source atom (for LP calculation)
  // Filter to match StaticLonePair behavior (exclude hidden atoms)
  const allBonds = getBondsForAtom(workspace, arrowData.sourceAtomId);
  const bonds = allBonds.filter(bond => {
    const visibility = workspace.getVisibility(bond.neighborId);
    return visibility !== 1; // Exclude Hidden atoms (VisibilityFlag.Hidden = 1)
  });

  // Get electron count for source atom (must match StaticLonePair)
  const sourceAtom = workspace.getAtom(arrowData.sourceAtomId);
  const lpCount = sourceAtom?.lonePairs ?? 0;

  if (lpCount === 0) {
    return null; // No LPs, can't draw arrow
  }

  // Calculate dynamic arrow path
  // Motion v12: use function form with .get() for automatic dependency tracking
  const arrowPath = useTransform(() => {
    const sourceX = sourceAtomMotion.x.get();
    const sourceY = sourceAtomMotion.y.get();
    const destX = destAtomMotion.x.get();
    const destY = destAtomMotion.y.get();

    // Calculate bond directions from current neighbor positions
    // IMPORTANT: Must match StaticLonePair exactly for LP positions to align
    const bondDirections: number[][] = [];
    bonds.forEach(bond => {
      const neighborMotion = getAtomMotion(bond.neighborId);
      if (neighborMotion) {
        const neighborX = neighborMotion.x.get();
        const neighborY = neighborMotion.y.get();

        const dx = neighborX - sourceX;
        const dy = neighborY - sourceY;
        const length = Math.sqrt(dx * dx + dy * dy);

        if (length > 0) {
          bondDirections.push([dx / length, dy / length, 0]);
        }
      }
    });

    // NOTE: StaticLonePair adds hydrogen direction here, but we skip it for now
    // TODO: Add hydrogen direction calculation to match StaticLonePair exactly

    // Calculate LP positions using arrangeLonePairs
    // Use lpCount (same as StaticLonePair for lone pair electrons)
    const electronVectors = lonePairs2d.arrangeLonePairs(bondDirections, lpCount);

    if (electronVectors.length === 0) {
      return ''; // No valid LP positions
    }

    // Calculate LP offset (same as svgElectrons.calcElectronOffset)
    // For simplicity, use default offset (paper carbon optimization could be added later)
    // const lpOffset = format.lonePair.offset;

    // Pass ALL LP directions to buildCurvePropsPA (like static arrows do)
    // kaff-arrow expects UNIT VECTORS (normalized directions), not offset vectors
    // The offset is applied internally via calculateStartPoint
    const allLPDirections = electronVectors.map(v => [
      v[0],
      v[1],
      (v[2] ?? 0)
    ]);

    // Build CurveProps and use CurveMath for consistent arrow calculation
    const curveProps = buildCurvePropsPA(
      sourceX,
      sourceY,
      destX,
      destY,
      allLPDirections,
      arrowData,
      format
    );

    // Calculate curve using CurveMath (same logic as static arrows)
    const curve = CurveMath.calculatePoints(curveProps);

    // Build SVG path from calculated curve
    return `M ${curve.start[0]} ${curve.start[1]} C ${curve.cp1[0]} ${curve.cp1[1]} ${curve.cp2[0]} ${curve.cp2[1]} ${curve.end[0]} ${curve.end[1]}`;
  });

  return (
    <motion.path
      d={arrowPath}
      stroke={feedback.currentStyle.arrowColor ?? "#BE0000"}
      strokeWidth={format.arrow.thickness}
      fill="none"
      markerEnd="url(#arrowhead)"
      style={{ pointerEvents: 'none' }}
    />
  );
}

/**
 * PB: Lone Pair → Bond
 */
function DynamicArrowPB({ arrowData, allElectronData: _allElectronData, allChargeData: _allChargeData }: DynamicArrowProps) {
  const { getAtomMotion } = useMotion();
  const { workspace } = useMolecularSVG();
  const format = useFormatOverride();

  // Generate unique ID for this arrow
  const arrowId = `arrow-${arrowData.sourceAtomId}-${arrowData.destBondAtomIds?.join('-')}`;

  const selection = useSelection({ optional: true });
  const feedback = useFeedbackWithContext(arrowId);

  // Get MotionValues for source atom
  const sourceAtomMotion = getAtomMotion(arrowData.sourceAtomId);

  // Get destination bond atoms
  const [destAtom1Id, destAtom2Id] = arrowData.destBondAtomIds || [];
  const destAtom1Motion = destAtom1Id ? getAtomMotion(destAtom1Id) : null;
  const destAtom2Motion = destAtom2Id ? getAtomMotion(destAtom2Id) : null;

  if (!sourceAtomMotion || !destAtom1Motion || !destAtom2Motion) {
    return null;
  }

  // Check selection state
  const isSelected = selection?.isSelected(arrowId) ?? false;
  const isHovered = selection?.isHovered(arrowId) ?? false;

  // Sync selection state with FeedbackManager
  useEffect(() => {
    feedback.setSelected(isSelected);
  }, [isSelected, feedback]);

  useEffect(() => {
    feedback.setHovered(isHovered);
  }, [isHovered, feedback]);

  // Get bonds for source atom (for LP calculation)
  // Filter to match StaticLonePair behavior (exclude hidden atoms)
  const allBondsPB = getBondsForAtom(workspace, arrowData.sourceAtomId);
  const bondsPB = allBondsPB.filter(bond => {
    const visibility = workspace.getVisibility(bond.neighborId);
    return visibility !== 1; // Exclude Hidden atoms (VisibilityFlag.Hidden = 1)
  });

  // Get electron count for source atom (must match StaticLonePair)
  const sourceAtomPB = workspace.getAtom(arrowData.sourceAtomId);
  const lpCountPB = sourceAtomPB?.lonePairs ?? 0;

  if (lpCountPB === 0) {
    return null;
  }

  // Calculate dynamic arrow path
  // Motion v12: use function form with .get() for automatic dependency tracking
  const arrowPath = useTransform(() => {
    const sourceX = sourceAtomMotion.x.get();
    const sourceY = sourceAtomMotion.y.get();
    const destAtom1X = destAtom1Motion.x.get();
    const destAtom1Y = destAtom1Motion.y.get();
    const destAtom2X = destAtom2Motion.x.get();
    const destAtom2Y = destAtom2Motion.y.get();

    // Calculate bond directions from current neighbor positions
    // IMPORTANT: Must match StaticLonePair exactly for LP positions to align
    const bondDirections: number[][] = [];
    bondsPB.forEach(bond => {
      const neighborMotion = getAtomMotion(bond.neighborId);
      if (neighborMotion) {
        const neighborX = neighborMotion.x.get();
        const neighborY = neighborMotion.y.get();

        const dx = neighborX - sourceX;
        const dy = neighborY - sourceY;
        const length = Math.sqrt(dx * dx + dy * dy);

        if (length > 0) {
          bondDirections.push([dx / length, dy / length, 0]);
        }
      }
    });

    // NOTE: StaticLonePair adds hydrogen direction here, but we skip it for now
    // TODO: Add hydrogen direction calculation to match StaticLonePair exactly

    // Calculate LP positions
    const electronVectors = lonePairs2d.arrangeLonePairs(bondDirections, lpCountPB);

    if (electronVectors.length === 0) {
      return '';
    }

    // Pass ALL LP directions to buildCurvePropsPB (like static arrows do)
    // kaff-arrow expects UNIT VECTORS (normalized directions), not offset vectors
    // The offset is applied internally via calculateStartPoint
    const allLPDirections = electronVectors.map(v => [
      v[0],
      v[1],
      (v[2] ?? 0)
    ]);

    // Arrow destination: bond midpoint
    const bondMidX = (destAtom1X + destAtom2X) / 2;
    const bondMidY = (destAtom1Y + destAtom2Y) / 2;

    // Build CurveProps and use CurveMath for consistent arrow calculation
    const curveProps = buildCurvePropsPB(
      sourceX,
      sourceY,
      bondMidX,
      bondMidY,
      destAtom1X,
      destAtom1Y,
      destAtom2X,
      destAtom2Y,
      allLPDirections,
      arrowData,
      format
    );

    // Calculate curve using CurveMath (same logic as static arrows)
    const curve = CurveMath.calculatePoints(curveProps);

    // Build SVG path from calculated curve
    return `M ${curve.start[0]} ${curve.start[1]} C ${curve.cp1[0]} ${curve.cp1[1]} ${curve.cp2[0]} ${curve.cp2[1]} ${curve.end[0]} ${curve.end[1]}`;
  });

  return (
    <motion.path
      d={arrowPath}
      stroke={feedback.currentStyle.arrowColor ?? "#BE0000"}
      strokeWidth={format.arrow.thickness}
      fill="none"
      markerEnd="url(#arrowhead)"
      style={{ pointerEvents: 'none' }}
    />
  );
}

/**
 * BA: Bond → Atom
 */
function DynamicArrowBA({ arrowData }: DynamicArrowProps) {
  const { getAtomMotion } = useMotion();
  const { workspace } = useMolecularSVG();
  const format = useFormatOverride();
  const { bondRecipes } = useSVGGeneration(workspace, 800, 600);

  // Generate unique ID for this arrow
  const arrowId = `arrow-${arrowData.sourceBondAtomIds?.join('-')}-${arrowData.destAtomId}`;

  const selection = useSelection({ optional: true });
  const feedback = useFeedbackWithContext(arrowId);

  // Get source bond atoms
  const [sourceAtom1Id, sourceAtom2Id] = arrowData.sourceBondAtomIds || [];
  const sourceAtom1Motion = sourceAtom1Id ? getAtomMotion(sourceAtom1Id) : null;
  const sourceAtom2Motion = sourceAtom2Id ? getAtomMotion(sourceAtom2Id) : null;

  // Get destination atom
  const destAtomMotion = arrowData.destAtomId ? getAtomMotion(arrowData.destAtomId) : null;

  if (!sourceAtom1Motion || !sourceAtom2Motion || !destAtomMotion) {
    return null;
  }

  // Check selection state
  const isSelected = selection?.isSelected(arrowId) ?? false;
  const isHovered = selection?.isHovered(arrowId) ?? false;

  // Sync selection state with FeedbackManager
  useEffect(() => {
    feedback.setSelected(isSelected);
  }, [isSelected, feedback]);

  useEffect(() => {
    feedback.setHovered(isHovered);
  }, [isHovered, feedback]);

  // Get bond ID from sourceBondAtomIds
  const sourceBond = workspace.getAllBonds().find(
    (b) => (b.a1.id === sourceAtom1Id && b.a2.id === sourceAtom2Id) ||
           (b.a1.id === sourceAtom2Id && b.a2.id === sourceAtom1Id)
  );

  if (!sourceBond) {
    return null;
  }

  // Calculate arrow path
  // Motion v12: use function form with .get() for automatic dependency tracking
  const arrowPath = useTransform(() => {
    const sourceAtom1X = sourceAtom1Motion.x.get();
    const sourceAtom1Y = sourceAtom1Motion.y.get();
    const sourceAtom2X = sourceAtom2Motion.x.get();
    const sourceAtom2Y = sourceAtom2Motion.y.get();
    const destX = destAtomMotion.x.get();
    const destY = destAtomMotion.y.get();

    // Source atom
    const bondMidX = arrowData.sourceAtomId === sourceAtom1Id ? sourceAtom2X : sourceAtom1X;
    const bondMidY = arrowData.sourceAtomId === sourceAtom1Id ? sourceAtom2Y : sourceAtom1Y;

    // Use calculateBondArrowStart to get adjusted start position (handles bond tubes)
    const bondRecipe = bondRecipes.find(r => r.bondId === sourceBond.id);
    const adjustedStart = calculateBondArrowStart(
      sourceBond.id,
      { x: bondMidX, y: bondMidY }, // startPos (bond midpoint)
      bondRecipe,
      workspace,
      format,
      { x: destX, y: destY } // destination for triple bond tube selection
    );

    // Build CurveProps and use CurveMath for consistent arrow calculation
    const curveProps = buildCurvePropsBA(
      adjustedStart.x,
      adjustedStart.y,
      sourceAtom1X,
      sourceAtom1Y,
      sourceAtom2X,
      sourceAtom2Y,
      destX,
      destY,
      arrowData,
      format
    );

    // Calculate curve using CurveMath (same logic as static arrows)
    const curve = CurveMath.calculatePoints(curveProps);

    // Build SVG path from calculated curve
    return `M ${curve.start[0]} ${curve.start[1]} C ${curve.cp1[0]} ${curve.cp1[1]} ${curve.cp2[0]} ${curve.cp2[1]} ${curve.end[0]} ${curve.end[1]}`;
  });

  return (
    <motion.path
      d={arrowPath}
      stroke={feedback.currentStyle.arrowColor ?? "#BE0000"}
      strokeWidth={format.arrow.thickness}
      fill="none"
      markerEnd="url(#arrowhead)"
      style={{ pointerEvents: 'none' }}
    />
  );
}

/**
 * BB: Bond → Bond
 */
function DynamicArrowBB({ arrowData }: DynamicArrowProps) {
  const { getAtomMotion } = useMotion();
  const { workspace } = useMolecularSVG();
  const format = useFormatOverride();
  const { bondRecipes } = useSVGGeneration(workspace, 800, 600);

  // Generate unique ID for this arrow
  const arrowId = `arrow-${arrowData.sourceBondAtomIds?.join('-')}-${arrowData.destBondAtomIds?.join('-')}`;

  const selection = useSelection({ optional: true });
  const feedback = useFeedbackWithContext(arrowId);

  // Get source bond atoms
  const [sourceAtom1Id, sourceAtom2Id] = arrowData.sourceBondAtomIds || [];
  const sourceAtom1Motion = sourceAtom1Id ? getAtomMotion(sourceAtom1Id) : null;
  const sourceAtom2Motion = sourceAtom2Id ? getAtomMotion(sourceAtom2Id) : null;

  // Get destination bond atoms
  const [destAtom1Id, destAtom2Id] = arrowData.destBondAtomIds || [];
  const destAtom1Motion = destAtom1Id ? getAtomMotion(destAtom1Id) : null;
  const destAtom2Motion = destAtom2Id ? getAtomMotion(destAtom2Id) : null;

  if (!sourceAtom1Motion || !sourceAtom2Motion || !destAtom1Motion || !destAtom2Motion) {
    return null;
  }

  // Check selection state
  const isSelected = selection?.isSelected(arrowId) ?? false;
  const isHovered = selection?.isHovered(arrowId) ?? false;

  // Sync selection state with FeedbackManager
  useEffect(() => {
    feedback.setSelected(isSelected);
  }, [isSelected, feedback]);

  useEffect(() => {
    feedback.setHovered(isHovered);
  }, [isHovered, feedback]);

  // Get bond ID from sourceBondAtomIds
  const sourceBond = workspace.getAllBonds().find(
    (b) => (b.a1.id === sourceAtom1Id && b.a2.id === sourceAtom2Id) ||
           (b.a1.id === sourceAtom2Id && b.a2.id === sourceAtom1Id)
  );

  if (!sourceBond) {
    return null;
  }

  // Calculate arrow path
  // Motion v12: use function form with .get() for automatic dependency tracking
  const arrowPath = useTransform(() => {
    const sourceAtom1X = sourceAtom1Motion.x.get();
    const sourceAtom1Y = sourceAtom1Motion.y.get();
    const sourceAtom2X = sourceAtom2Motion.x.get();
    const sourceAtom2Y = sourceAtom2Motion.y.get();
    const destAtom1X = destAtom1Motion.x.get();
    const destAtom1Y = destAtom1Motion.y.get();
    const destAtom2X = destAtom2Motion.x.get();
    const destAtom2Y = destAtom2Motion.y.get();

    // Use anchor atom to determine which side of the bond to start from
    // arrowData.sourceAtomId is the anchor atom (the one being dragged away from)
    // Start from the non-anchor atom (otherAtom) towards the destination
    const otherAtomX = arrowData.sourceAtomId === sourceAtom1Id ? sourceAtom2X : sourceAtom1X;
    const otherAtomY = arrowData.sourceAtomId === sourceAtom1Id ? sourceAtom2Y : sourceAtom1Y;

    // Start position: closer to the non-anchor atom (the one being dragged from)
    const sourceBondMidX = otherAtomX;
    const sourceBondMidY = otherAtomY;
    const destBondMidX = (destAtom1X + destAtom2X) / 2;
    const destBondMidY = (destAtom1Y + destAtom2Y) / 2;

    // Use calculateBondArrowStart to get adjusted start position (handles bond tubes)
    const bondRecipe = bondRecipes.find(r => r.bondId === sourceBond.id);
    const adjustedStart = calculateBondArrowStart(
      sourceBond.id,
      { x: sourceBondMidX, y: sourceBondMidY }, // startPos (from non-anchor atom)
      bondRecipe,
      workspace,
      format,
      { x: destBondMidX, y: destBondMidY } // destination for triple bond tube selection
    );

    // Build CurveProps and use CurveMath for consistent arrow calculation
    const curveProps = buildCurvePropsBB(
      adjustedStart.x,
      adjustedStart.y,
      sourceAtom1X,
      sourceAtom1Y,
      sourceAtom2X,
      sourceAtom2Y,
      destBondMidX,
      destBondMidY,
      destAtom1X,
      destAtom1Y,
      destAtom2X,
      destAtom2Y,
      arrowData,
      format
    );

    // Calculate curve using CurveMath (same logic as static arrows)
    const curve = CurveMath.calculatePoints(curveProps);

    // Build SVG path from calculated curve
    return `M ${curve.start[0]} ${curve.start[1]} C ${curve.cp1[0]} ${curve.cp1[1]} ${curve.cp2[0]} ${curve.cp2[1]} ${curve.end[0]} ${curve.end[1]}`;
  });

  return (
    <motion.path
      d={arrowPath}
      stroke={feedback.currentStyle.arrowColor ?? "#BE0000"}
      strokeWidth={format.arrow.thickness}
      fill="none"
      markerEnd="url(#arrowhead)"
      style={{ pointerEvents: 'none' }}
    />
  );
}
